import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { publicRequest, userRequest } from "../../requestMethods";
import { FiCalendar, FiUsers, FiFileText, FiPlus, FiAward, FiEdit2, FiTrash2, FiX } from "react-icons/fi";
import moment from "moment";
import styled, { keyframes } from "styled-components";
import { mobile, tablet } from "../../responsive";
import Select from "react-select";
import { commonSelectProps } from "../../components/shared/SelectStyles";

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
`;

const DashboardBg = styled.div`
    min-height: 100vh;
    background: transparent;
    color: #fff;
    padding-bottom: 3rem;
    position: relative;
    overflow: hidden;
    z-index: 1;
    width: 100%;
`;

const VideoBackground = styled.video`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: -2;
    opacity: 1;
`;

const BackgroundOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.45);
    z-index: -1;
`;

const DashboardContainer = styled.div`
    margin: 0 auto;
    padding: 2.5rem 2rem 4rem;
    display: flex;
    flex-direction: column;
    animation: ${fadeIn} 1s ease;
    max-width: 1440px;
    width: min(100%, 1360px);
    box-sizing: border-box;

    ${tablet({
        padding: "2.25rem 1.75rem 3.5rem",
        width: "100%",
    })}

    ${mobile({
        padding: "2rem 5px 3rem",
    })}
`;

const PageHeader = styled.div`
    margin-bottom: 2.5rem;
    text-align: center;

    ${tablet({
        marginBottom: "2.1rem",
    })}

    ${mobile({
        marginBottom: "1.8rem",
    })}
`;

const PageTitle = styled.h1`
    font-size: 2.75rem;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 0.5rem;
    font-family: "Inter", "Helvetica", Arial, sans-serif;
    text-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
    letter-spacing: 0.035em;
    background: linear-gradient(135deg, #ffffff 0%, #f3f4f6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;

    ${tablet({
        fontSize: "2.4rem",
    })}

    ${mobile({
        fontSize: "2.2rem",
    })}
`;

const PageSubtitle = styled.p`
    font-size: 1.15rem;
    color: rgba(209, 213, 219, 0.95);
    font-family: "Inter", "Helvetica", Arial, sans-serif;
    font-weight: 500;
    letter-spacing: 0.02em;
    text-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);

    ${tablet({
        fontSize: "1.05rem",
    })}

    ${mobile({
        fontSize: "1rem",
    })}
`;

const Section = styled.section`
    background: rgba(17, 24, 39, 0.9);
    border-radius: 20px;
    box-shadow: 0 20px 45px rgba(15, 23, 42, 0.45);
    padding: 1.85rem 1.75rem;
    border: 1px solid rgba(255, 255, 255, 0.08);
    margin-bottom: 1.75rem;
    backdrop-filter: blur(18px);
    position: relative;
    z-index: 1;

    &.filter-section {
        z-index: 100;
    }

    ${tablet({
        padding: "1.6rem 1.4rem",
        marginBottom: "1.5rem",
    })}

    ${mobile({
        padding: "1.4rem 5px",
        marginBottom: "1.25rem",
    })}
`;

const SectionTitle = styled.h3`
    color: #00eaff;
    font-family: "Inter", "Helvetica", Arial, sans-serif;
    font-weight: 600;
    font-size: 1.2rem;
    margin-bottom: 1rem;
    letter-spacing: 0.025em;
    display: flex;
    align-items: center;
    gap: 0.75rem;

    svg {
        font-size: 1.5rem;
    }
`;

const FiltersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 1.25rem;

    ${tablet({
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: "1.1rem",
    })}

    ${mobile({
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
    })}
`;

const FilterCard = styled.div`
    background: rgba(15, 23, 42, 0.85);
    border-radius: 14px;
    border: 1.5px solid rgba(59, 130, 246, 0.2);
    padding: 1.2rem 1.1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    ${tablet({
        padding: "1.15rem 1rem",
    })}

    ${mobile({
        padding: "1.05rem 0.95rem",
    })}
`;

const FilterLabel = styled.label`
    font-size: 0.8rem;
    font-weight: 600;
    color: #9ca3af;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    svg {
        font-size: 1rem;
    }
`;

const DateInput = styled.input`
    background: rgba(30, 41, 59, 0.85);
    border: 1px solid rgba(148, 163, 184, 0.45);
    border-radius: 0.75rem;
    padding: 0.75rem 1rem;
    color: #f8fafc;
    font-size: 0.9rem;
    font-family: "Inter", "Helvetica", Arial, sans-serif;
    transition: all 0.2s ease;
    min-height: 42px;

    &:focus {
        outline: none;
        border-color: rgba(59, 130, 246, 0.75);
        box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.15);
    }

    &:hover {
        border-color: rgba(59, 130, 246, 0.75);
    }

    &::-webkit-calendar-picker-indicator {
        filter: invert(1);
        cursor: pointer;
    }
`;

const ActionButtons = styled.div`
    display: flex;
    gap: 0.85rem;
    flex-wrap: wrap;
    justify-content: flex-end;
    margin-top: 1rem;

    ${tablet({
        justifyContent: "flex-start",
        marginTop: "1.15rem",
    })}

    ${mobile({
        flexDirection: "column",
        alignItems: "stretch",
        gap: "0.75rem",
        marginTop: "1.25rem",
    })}
`;

const ActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    border-radius: 9999px;
    padding: 0.65rem 1.5rem;
    font-size: 0.9rem;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
    color: #ffffff;

    &.primary {
        background: linear-gradient(135deg, #667eea, #764ba2);
        box-shadow: 0 10px 25px rgba(102, 126, 234, 0.35);

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 12px 32px rgba(118, 75, 162, 0.4);
        }
    }

    &.secondary {
        background: linear-gradient(135deg, #ef4444, #f97316);
        box-shadow: 0 10px 25px rgba(239, 68, 68, 0.35);

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 12px 32px rgba(249, 115, 22, 0.4);
        }
    }

    svg {
        font-size: 1.1rem;
    }
`;

const LuxuryTable = styled.div`
    background: linear-gradient(
        135deg,
        rgba(15, 23, 42, 0.88) 0%,
        rgba(30, 41, 59, 0.88) 100%
    );
    border-radius: 20px;
    box-shadow: 0 16px 40px rgba(15, 23, 42, 0.55);
    border: 1px solid rgba(59, 130, 246, 0.22);
    overflow-x: auto;
    padding-bottom: 0.25rem;
    -webkit-overflow-scrolling: touch;
    
    &::-webkit-scrollbar {
        height: 8px;
    }
    
    &::-webkit-scrollbar-track {
        background: rgba(15, 23, 42, 0.5);
        border-radius: 10px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: rgba(59, 130, 246, 0.5);
        border-radius: 10px;
        
        &:hover {
            background: rgba(59, 130, 246, 0.7);
        }
    }
`;

const DailyBreakdownDesktop = styled(LuxuryTable)`
    ${mobile({
        display: "none",
    })}
`;

const DailyBreakdownMobile = styled.div`
    display: none;

    ${mobile({
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
        marginTop: "0.75rem",
    })}
`;

const MobileInstallerCard = styled.div`
    background: rgba(15, 23, 42, 0.9);
    border: 1px solid rgba(59, 130, 246, 0.25);
    border-radius: 14px;
    padding: 1rem 1rem 0.85rem;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.35);
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
`;

const MobileInstallerHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
`;

const MobileInstallerName = styled.span`
    font-weight: 700;
    font-size: 1rem;
    color: #e2e8f0;
`;

const MobileInstallerMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #94a3b8;
    font-size: 0.85rem;
`;

const MobileBadge = styled.span`
    background: rgba(59, 130, 246, 0.2);
    color: #bfdbfe;
    border: 1px solid rgba(59, 130, 246, 0.3);
    padding: 0.25rem 0.65rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 700;
    white-space: nowrap;
`;

const MobileDateList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const MobileDateItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.65rem 0.75rem;
    background: rgba(30, 41, 59, 0.85);
    border: 1px solid rgba(148, 163, 184, 0.2);
    border-radius: 10px;
`;

const MobileDateLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 0.35rem;
    font-weight: 600;
    color: #cbd5e1;
    font-size: 0.9rem;
`;

const MobileAmount = styled.span`
    font-weight: 700;
    color: #34d399;
    font-size: 0.95rem;
`;

const MobileJournalCount = styled.span`
    color: #94a3b8;
    font-size: 0.8rem;
    font-weight: 600;
`;

const MobileEmpty = styled.div`
    padding: 0.6rem 0.75rem;
    color: #94a3b8;
    font-style: italic;
    font-size: 0.85rem;
    background: rgba(30, 41, 59, 0.55);
    border: 1px dashed rgba(148, 163, 184, 0.3);
    border-radius: 10px;
    text-align: center;
`;

const MobileTotalsCard = styled.div`
    background: rgba(12, 20, 33, 0.95);
    border: 1px solid rgba(52, 211, 153, 0.35);
    border-radius: 14px;
    padding: 0.9rem 1rem;
    box-shadow: 0 12px 28px rgba(12, 20, 33, 0.35);
`;

const MobileTotalsTitle = styled.div`
    font-weight: 700;
    font-size: 0.95rem;
    color: #34d399;
    margin-bottom: 0.45rem;
    display: flex;
    align-items: center;
    gap: 0.4rem;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    min-width: 100%;

    @media only screen and (min-width: 969px) {
        /* Prevent sticky columns from visually bleeding into scrollable cells */
        border-collapse: separate;
        border-spacing: 0;
    }
`;

const TableHead = styled.thead`
    background: linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%);
    
    th {
        padding: 1rem 1.5rem;
        text-align: left;
        font-size: 0.75rem;
        font-weight: 700;
        color: #94a3b8;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        border-bottom: 1px solid rgba(59, 130, 246, 0.2);
        white-space: nowrap;
        position: sticky;
        top: 0;
        z-index: 10;
        background: inherit;
        
        &.text-center {
            text-align: center;
        }
        
        &.text-right {
            text-align: right;
        }
        
        &.sticky-col {
            position: sticky;
            left: 0;
            z-index: 20;
            box-shadow: 2px 0 4px rgba(0, 0, 0, 0.3);
        }
        
        &.sticky-col-right {
            position: sticky;
            right: 0;
            z-index: 20;
            box-shadow: -2px 0 4px rgba(0, 0, 0, 0.3);
        }

        @media only screen and (min-width: 969px) {
            &.sticky-col,
            &.sticky-col-right {
                /* Keep headers opaque on desktop so dates don't bleed underneath */
                background: linear-gradient(135deg, rgba(15, 23, 42, 1) 0%, rgba(30, 41, 59, 1) 100%);
                background-clip: padding-box;
                z-index: 30;
            }
        }
    }
`;

const TableBody = styled.tbody`
    tr {
        transition: all 0.2s ease;
        
        &:hover {
            background: rgba(59, 130, 246, 0.08);
        }
        
        &.total-row {
            background: linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%);
            font-weight: 700;
            
            &:hover {
                background: linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%);
            }
            
            td {
                padding: 1rem 1.5rem;
                color: #ffffff;
                border-top: 2px solid rgba(59, 130, 246, 0.3);
            }
        }
    }
    
    td {
        padding: 1rem 1.5rem;
        font-size: 0.9rem;
        color: #e2e8f0;
        border-bottom: 1px solid rgba(51, 65, 85, 0.5);
        
        &.text-center {
            text-align: center;
        }
        
        &.text-right {
            text-align: right;
        }
        
        &.font-semibold {
            font-weight: 600;
            color: #f1f5f9;
        }
        
        &.font-bold {
            font-weight: 700;
            color: #ffffff;
        }
        
        &.sticky-col {
            position: sticky;
            left: 0;
            z-index: 5;
            background: rgba(15, 23, 42, 0.95);
            box-shadow: 2px 0 4px rgba(0, 0, 0, 0.3);
        }
        
        &.sticky-col-right {
            position: sticky;
            right: 0;
            z-index: 5;
            background: rgba(30, 41, 59, 0.95);
            box-shadow: -2px 0 4px rgba(0, 0, 0, 0.3);
        }

        @media only screen and (min-width: 969px) {
            &.sticky-col {
                background: rgba(15, 23, 42, 1);
                z-index: 12;
            }
            
            &.sticky-col-right {
                background: rgba(30, 41, 59, 1);
                z-index: 12;
            }
        }
    }
`;

const AmountCell = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
`;

const AmountValue = styled.span`
    font-weight: 700;
    color: #34d399;
    font-size: 0.95rem;
`;

const JournalCount = styled.span`
    font-size: 0.7rem;
    color: #94a3b8;
`;

const EmptyCell = styled.span`
    color: rgba(148, 163, 184, 0.3);
    font-size: 1rem;
`;

const LoadingSpinner = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    gap: 1rem;
    
    &::before {
        content: '';
        width: 48px;
        height: 48px;
        border: 4px solid rgba(59, 130, 246, 0.2);
        border-top-color: #3b82f6;
        border-radius: 50%;
        animation: spin 1s linear infinite;
    }
    
    @keyframes spin {
        to { transform: rotate(360deg); }
    }
    
    span {
        color: #94a3b8;
        font-size: 0.9rem;
    }
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    gap: 1rem;
    
    svg {
        font-size: 4rem;
        color: rgba(148, 163, 184, 0.3);
    }
    
    h4 {
        color: #e2e8f0;
        font-size: 1.25rem;
        font-weight: 600;
        margin: 0;
    }
    
    p {
        color: #94a3b8;
        font-size: 0.9rem;
        margin: 0;
    }
`;

const JournalsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
    gap: 1.5rem;
    
    ${tablet({
        gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
        gap: "1.25rem",
    })}
    
    ${mobile({
        gridTemplateColumns: "1fr",
        gap: "1rem",
    })}
`;

const JournalCard = styled.div`
    background: linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%);
    border: 1px solid rgba(59, 130, 246, 0.2);
    border-radius: 16px;
    overflow: hidden;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 32px rgba(59, 130, 246, 0.25);
        border-color: rgba(59, 130, 246, 0.4);
    }
`;

const JournalCardHeader = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1.25rem 1.5rem;
    background: rgba(30, 41, 59, 0.4);
    border-bottom: 1px solid rgba(148, 163, 184, 0.15);
`;

const JournalDate = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 0.6rem 1.25rem;
    background: rgba(15, 23, 42, 0.6);
    border: 1px solid rgba(148, 163, 184, 0.2);
    border-radius: 10px;
    color: #e2e8f0;
    font-size: 1rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    
    svg {
        font-size: 1.1rem;
        color: #94a3b8;
    }
`;

const JournalCardBody = styled.div`
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const JournalSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const JournalLabel = styled.span`
    font-weight: 600;
    color: #9ca3af;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const JournalValue = styled.span`
    color: #e2e8f0;
    font-size: 0.9rem;
    font-weight: 500;
`;

const InstallersGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
`;

const InstallerTag = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.3), rgba(118, 75, 162, 0.3));
    border: 1px solid rgba(102, 126, 234, 0.4);
    color: #e2e8f0;
    padding: 0.5rem 0.875rem;
    border-radius: 9999px;
    font-size: 0.85rem;
    font-weight: 600;
    
    svg {
        font-size: 0.875rem;
    }
`;

const InstallerAmount = styled.span`
    background: rgba(52, 211, 153, 0.2);
    color: #34d399;
    padding: 0.2rem 0.6rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 700;
`;

const InstallerEntries = styled.span`
    background: rgba(96, 165, 250, 0.2);
    color: #60a5fa;
    padding: 0.2rem 0.5rem;
    border-radius: 9999px;
    font-size: 0.7rem;
    font-weight: 700;
`;

const AmountBreakdownSection = styled.div`
    background: rgba(15, 23, 42, 0.5);
    padding: 1rem;
    border-radius: 12px;
    border: 1px solid rgba(59, 130, 246, 0.1);
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 0.5rem;
`;

const BreakdownGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 0.75rem;
`;

const BreakdownItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const BreakdownLabel = styled.span`
    font-size: 0.7rem;
    color: #9ca3af;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const BreakdownValue = styled.span`
    font-size: 0.875rem;
    color: #e2e8f0;
    font-weight: 700;
`;

const JournalCardFooter = styled.div`
    padding: 0.875rem 1.5rem;
    background: rgba(15, 23, 42, 0.5);
    border-top: 1px solid rgba(59, 130, 246, 0.1);
    color: #9ca3af;
    font-size: 0.75rem;
    font-style: italic;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const JournalActions = styled.div`
    display: flex;
    gap: 0.5rem;
    align-items: center;
`;

const IconButton = styled.button`
    background: transparent;
    border: 1px solid rgba(59, 130, 246, 0.3);
    color: #60a5fa;
    padding: 0.5rem;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    
    &:hover {
        background: rgba(59, 130, 246, 0.1);
        border-color: rgba(59, 130, 246, 0.5);
        transform: translateY(-2px);
    }
    
    &.delete {
        color: #ef4444;
        border-color: rgba(239, 68, 68, 0.3);
        
        &:hover {
            background: rgba(239, 68, 68, 0.1);
            border-color: rgba(239, 68, 68, 0.5);
        }
    }
    
    svg {
        font-size: 1rem;
    }
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.75);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
`;

const ModalContent = styled.div`
    background: linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%);
    border: 1px solid rgba(59, 130, 246, 0.3);
    border-radius: 20px;
    padding: 2rem;
    max-width: 600px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
    position: relative;
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
    color: #ffffff;
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0;
`;

const CloseButton = styled.button`
    background: transparent;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    padding: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    
    &:hover {
        color: #ffffff;
        transform: rotate(90deg);
    }
    
    svg {
        font-size: 1.5rem;
    }
`;

const FormGroup = styled.div`
    margin-bottom: 1.5rem;
`;

const Label = styled.label`
    display: block;
    color: #9ca3af;
    font-size: 0.875rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const ModalActions = styled.div`
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    margin-top: 2rem;
`;

const Button = styled.button`
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.9rem;
    cursor: pointer;
    border: none;
    transition: all 0.2s ease;
    
    &.primary {
        background: linear-gradient(135deg, #667eea, #764ba2);
        color: white;
        
        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
        }
    }
    
    &.secondary {
        background: rgba(148, 163, 184, 0.2);
        color: #e2e8f0;
        border: 1px solid rgba(148, 163, 184, 0.3);
        
        &:hover {
            background: rgba(148, 163, 184, 0.3);
        }
    }
`;

const Journal = () => {
    const history = useHistory();
    const currentUser = useSelector((state) => state.user.currentUser);
    
    const installer = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "installer"
            : null
    );

    const staff = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "staff"
            : null
    );

    const superadmin = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.isSuperAdmin === true : null
    );

    const installerManager = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "installerManager"
            : null
    );

    const [dateFrom, setDateFrom] = useState(moment().startOf('month').format("YYYY-MM-DD"));
    const [dateTo, setDateTo] = useState(moment().add(1, 'month').endOf('month').format("YYYY-MM-DD"));
    const [journals, setJournals] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [installerPoints, setInstallerPoints] = useState({});
    const [dailyBreakdown, setDailyBreakdown] = useState([]);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedJournal, setSelectedJournal] = useState(null);
    const [dayDetailModalOpen, setDayDetailModalOpen] = useState(false);
    const [selectedDayJournals, setSelectedDayJournals] = useState([]);
    const [selectedDayDate, setSelectedDayDate] = useState('');
    const [allInstallers, setAllInstallers] = useState([]);
    const [editFormData, setEditFormData] = useState({
        installers: [],
        createdAt: '',
    });

    useEffect(() => {
        fetchJournals();
        fetchInstallers();
    }, [dateFrom, dateTo, currentUser, superadmin]);

    const fetchJournals = async () => {
        setIsLoading(true);
        try {
            // Determine principle parameter based on user type
            let principleParam;
            if (superadmin) {
                // SuperAdmin can see all journals
                principleParam = undefined;
            } else if (currentUser?.type === "outright" || currentUser?.type === "manager") {
                // Outright and manager users can only see their own outlet
                principleParam = currentUser._id;
            } else {
                // For other user types, use their principle if available
                principleParam = currentUser?.principle;
            }

            const filters = {
                dateFrom: dateFrom,
                dateTo: dateTo,
            };
            
            // Add principle to filters if it exists
            if (principleParam) {
                filters.principle = principleParam;
            }

            const res = await publicRequest.get("/journal/history", {
                params: { filters: JSON.stringify(filters) },
            });

            if (res.data && res.data.payload) {
                // Filter journals for staff users - only show journals where order.createdBy matches current user ID
                let filteredJournals = res.data.payload;
                if (staff && currentUser?._id) {
                    filteredJournals = res.data.payload.filter((journal) => {
                        const orderCreatedBy = journal.order?.createdBy;
                        // Handle both string ID and object with _id
                        const createdById = typeof orderCreatedBy === 'string' 
                            ? orderCreatedBy 
                            : orderCreatedBy?._id || orderCreatedBy;
                        return createdById === currentUser._id;
                    });
                }
                
                // Filter journals for installer users - only show journals where installer is in the installers array
                if (installer && currentUser?._id) {
                    filteredJournals = filteredJournals.filter((journal) => {
                        // Check if current user is in the installers array
                        if (journal.installers && journal.installers.length > 0) {
                            return journal.installers.some((inst) => {
                                const installerId = typeof inst === 'string' ? inst : inst?._id || inst;
                                return installerId === currentUser._id;
                            });
                        }
                        return false;
                    });
                }
                
                setJournals(filteredJournals);
                calculateInstallerPoints(filteredJournals);
            }
        } catch (error) {
            console.error("Error fetching journals:", error);
            alert("Failed to fetch journals");
        } finally {
            setIsLoading(false);
        }
    };

    const calculateInstallerPoints = (journalsData) => {
        const points = {};
        const dailyData = {};
        const installerOrders = {}; // Track unique orders per installer
        
        journalsData.forEach((journal) => {
            const journalDate = moment(journal.createdAt).format("DD-MMM");
            const orderId = journal.order?._id || journal.order;
            const isServiceType = journal.type === "service";
            // Get star rating from order.survey.starRating
            const starRating = journal.order?.survey?.starRating 
                ? parseInt(journal.order.survey.starRating) || 0
                : null;
            
            if (journal.installers && journal.installers.length > 0) {
                journal.installers.forEach((installer) => {
                    const installerId = installer._id || installer;
                    const installerName = installer.name || "Unknown";
                    
                    const amountData = journal.installerAmounts?.[installerId];
                    const installerAmount = isServiceType ? 0 : (amountData?.amount || 0);
                    
                    if (!points[installerId]) {
                        points[installerId] = {
                            name: installerName,
                            points: 0,
                            totalAmount: 0,
                            totalStarRatings: 0,
                            totalOrders: new Set(),
                        };
                    }
                    points[installerId].points += 1;
                    points[installerId].totalAmount += installerAmount;
                    
                    // Track unique orders and star ratings
                    if (orderId) {
                        points[installerId].totalOrders.add(orderId);
                        if (starRating !== null && starRating !== undefined) {
                            points[installerId].totalStarRatings += starRating;
                        }
                    }
                    
                    if (!dailyData[installerId]) {
                        dailyData[installerId] = {
                            name: installerName,
                            dates: {},
                        };
                    }
                    
                    if (!dailyData[installerId].dates[journalDate]) {
                        dailyData[installerId].dates[journalDate] = {
                            amount: 0,
                            journals: 0,
                            journalList: [],
                        };
                    }
                    
                    if (!isServiceType) {
                        dailyData[installerId].dates[journalDate].amount += installerAmount;
                    }
                    dailyData[installerId].dates[journalDate].journals += 1;
                    dailyData[installerId].dates[journalDate].journalList.push(journal);
                });
            }
        });
        
        // Convert Set to count and calculate averages
        Object.keys(points).forEach((installerId) => {
            points[installerId].totalOrders = points[installerId].totalOrders.size;
            points[installerId].averageStarRatings = points[installerId].totalOrders > 0
                ? (points[installerId].totalStarRatings / points[installerId].totalOrders).toFixed(2)
                : 0;
        });
        
        setInstallerPoints(points);
        
        const dailyArray = Object.entries(dailyData).map(([installerId, data]) => ({
            id: installerId,
            name: data.name,
            dates: data.dates,
        }));
        
        setDailyBreakdown(dailyArray);
    };

    const fetchInstallers = async () => {
        try {
            let principleParam;
            if (superadmin) {
                principleParam = undefined;
            } else if (currentUser?.type === "outright" || currentUser?.type === "manager") {
                principleParam = currentUser._id;
            } else {
                principleParam = currentUser?.principle;
            }

            const params = {
                type: "installer",
                isAdmin: true,
            };
            
            if (principleParam) {
                params.principle = principleParam;
            }

            const res = await publicRequest.get("/users/admin", {
                params,
            });
            setAllInstallers(res.data);
        } catch (error) {
            console.error("Error fetching installers:", error);
        }
    };

    const handleEditJournal = (journal) => {
        setSelectedJournal(journal);
        setEditFormData({
            installers: journal.installers?.map(inst => inst._id || inst) || [],
            createdAt: moment(journal.createdAt).format("YYYY-MM-DDTHH:mm"),
        });
        setEditModalOpen(true);
    };

    const handleUpdateJournal = async () => {
        if (!selectedJournal) return;
        
        try {
            if (editFormData.installers.length === 0) {
                alert("Please select at least one installer");
                return;
            }

            const updateData = {
                installers: editFormData.installers,
                createdAt: new Date(editFormData.createdAt),
            };

            await userRequest.put(`/journal/${selectedJournal._id}`, updateData);
            
            alert("Journal updated successfully");
            setEditModalOpen(false);
            setSelectedJournal(null);
            fetchJournals(); // Refresh the list
        } catch (error) {
            console.error("Error updating journal:", error);
            alert("Failed to update journal");
        }
    };

    const handleDeleteJournal = async (journalId, onSuccess) => {
        if (!window.confirm("Are you sure you want to mark this journal as inactive?")) {
            return;
        }

        try {
            await userRequest.put(`/journal/delete/${journalId}`);
            alert("Journal marked as inactive");
            fetchJournals(); // Refresh the list
            onSuccess?.();
        } catch (error) {
            console.error("Error deleting journal:", error);
            alert("Failed to delete journal");
        }
    };

    const handleResetFilters = () => {
        setDateFrom(moment().startOf('month').format("YYYY-MM-DD"));
        setDateTo(moment().endOf('month').format("YYYY-MM-DD"));
    };

    const allDates = useMemo(() => {
        const dates = [];
        const start = moment(dateFrom);
        const end = moment(dateTo);
        
        while (start.isSameOrBefore(end)) {
            dates.push(start.format("DD-MMM"));
            start.add(1, 'day');
        }
        
        return dates;
    }, [dateFrom, dateTo]);

    const dailyTotals = useMemo(() => {
        const totals = {};
        
        dailyBreakdown.forEach((installer) => {
            Object.entries(installer.dates).forEach(([date, data]) => {
                if (!totals[date]) {
                    totals[date] = 0;
                }
                totals[date] += data.amount;
            });
        });
        
        return totals;
    }, [dailyBreakdown]);

    const performanceData = useMemo(() => {
        return Object.entries(installerPoints)
            .sort((a, b) => b[1].totalAmount - a[1].totalAmount)
            .map(([installerId, data]) => ({
                id: installerId,
                name: data.name,
                points: data.points,
                totalAmount: data.totalAmount,
                totalStarRatings: data.totalStarRatings || 0,
                totalOrders: data.totalOrders || 0,
                averageStarRatings: data.averageStarRatings || 0,
            }));
    }, [installerPoints]);

    return (
        <DashboardBg>
            <VideoBackground autoPlay loop muted playsInline>
                <source
                    src="https://ik.imagekit.io/dnddecpho/Inhome/animatedbg_Rx2Blqd5L.mp4?updatedAt=1746618975517"
                    type="video/mp4"
                />
            </VideoBackground>
            <BackgroundOverlay />
            <DashboardContainer>
                <PageHeader>
                    <PageTitle>Installation Journals</PageTitle>
                    <PageSubtitle>
                        Track installation assignments and installer performance
                    </PageSubtitle>
                </PageHeader>

                {/* Filters */}
                <Section className="filter-section">
                    <SectionTitle>
                        <FiCalendar />
                        Filters
                    </SectionTitle>
                    <FiltersGrid>
                        <FilterCard>
                            <FilterLabel>
                                <FiCalendar />
                                Date From
                            </FilterLabel>
                            <DateInput
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                            />
                        </FilterCard>
                        <FilterCard>
                            <FilterLabel>
                                <FiCalendar />
                                Date To
                            </FilterLabel>
                            <DateInput
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                            />
                        </FilterCard>
                    </FiltersGrid>
                    <ActionButtons>
                        <ActionButton className="primary" onClick={handleResetFilters}>
                            Reset to This Month
                        </ActionButton>
                    </ActionButtons>
                </Section>

                {/* Performance Summary - hide for installer type */}
                {performanceData.length > 0 && !installer && (
                    <Section>
                        <SectionTitle>
                            <FiAward />
                            Installer Performance
                        </SectionTitle>
                        <LuxuryTable>
                            <Table>
                                <TableHead>
                                    <tr>
                                        <th>Name</th>
                                        <th className="text-center">Total Journals</th>
                                        <th className="text-center">Total Orders</th>
                                        <th className="text-center">Total Star Ratings</th>
                                        <th className="text-center">Average Star Ratings</th>
                                        <th className="text-right">Total Amount</th>
                                    </tr>
                                </TableHead>
                                <TableBody>
                                    {performanceData.map((installer) => (
                                        <tr key={installer.id}>
                                            <td className="font-semibold">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                    <FiUsers />
                                                    {installer.name}
                                                </div>
                                            </td>
                                            <td className="text-center font-semibold">
                                                {installer.points}
                                            </td>
                                            <td className="text-center font-semibold">
                                                {installer.totalOrders}
                                            </td>
                                            <td className="text-center font-semibold">
                                                {installer.totalStarRatings}
                                            </td>
                                            <td className="text-center font-semibold">
                                                {installer.averageStarRatings > 0 ? parseFloat(installer.averageStarRatings).toFixed(2) : "0.00"}
                                            </td>
                                            <td className="text-right font-bold">
                                                RM {installer.totalAmount.toFixed(2)}
                                            </td>
                                        </tr>
                                    ))}
                                </TableBody>
                            </Table>
                        </LuxuryTable>
                    </Section>
                )}

                {/* Daily Breakdown */}
                {isLoading ? (
                    <Section>
                        <LoadingSpinner>
                            <span>Loading journals...</span>
                        </LoadingSpinner>
                    </Section>
                ) : dailyBreakdown.length > 0 && allDates.length > 0 ? (
                    <Section>
                        <SectionTitle>
                            <FiCalendar />
                            Daily Breakdown
                        </SectionTitle>
                        <DailyBreakdownDesktop>
                            <Table>
                                <TableHead>
                                    <tr>
                                        <th className="sticky-col">Name</th>
                                        {allDates.map((date) => (
                                            <th key={date} className="text-center">
                                                {date}
                                            </th>
                                        ))}
                                        {!installer && <th className="sticky-col-right text-center">Total</th>}
                                    </tr>
                                </TableHead>
                                <TableBody>
                                    {dailyBreakdown
                                        .sort((a, b) => {
                                            const totalA = Object.values(a.dates).reduce((sum, d) => sum + d.amount, 0);
                                            const totalB = Object.values(b.dates).reduce((sum, d) => sum + d.amount, 0);
                                            return totalB - totalA;
                                        })
                                        .map((installer) => {
                                            const rowTotal = Object.values(installer.dates).reduce(
                                                (sum, d) => sum + d.amount,
                                                0
                                            );
                                            
                                            return (
                                                <tr key={installer.id}>
                                                    <td className="sticky-col font-semibold">
                                                        {installer.name}
                                                    </td>
                                                    {allDates.map((date) => {
                                                        const dayData = installer.dates[date];
                                                        return (
                                                            <td key={date} className="text-center">
                                                                {dayData ? (
                                                                    <AmountCell>
                                                                        <AmountValue>
                                                                            {dayData.amount.toFixed(2)}
                                                                        </AmountValue>
                                                                        <JournalCount>
                                                                            ({dayData.journals})
                                                                        </JournalCount>
                                                                    </AmountCell>
                                                                ) : (
                                                                    <EmptyCell>-</EmptyCell>
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                    {!installer && (
                                                        <td className="sticky-col-right text-center font-bold">
                                                            RM {rowTotal.toFixed(2)}
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    {!installer && (
                                        <tr className="total-row">
                                            <td className="sticky-col">TOTAL</td>
                                            {allDates.map((date) => (
                                                <td key={date} className="text-center">
                                                    {dailyTotals[date] ? (
                                                        `${dailyTotals[date].toFixed(2)}`
                                                    ) : (
                                                        '-'
                                                    )}
                                                </td>
                                            ))}
                                            <td className="sticky-col-right text-center">
                                                RM {Object.values(dailyTotals).reduce((sum, amt) => sum + amt, 0).toFixed(2)}
                                            </td>
                                        </tr>
                                    )}
                                </TableBody>
                            </Table>
                        </DailyBreakdownDesktop>

                        <DailyBreakdownMobile>
                            {dailyBreakdown
                                .sort((a, b) => {
                                    const totalA = Object.values(a.dates).reduce((sum, d) => sum + d.amount, 0);
                                    const totalB = Object.values(b.dates).reduce((sum, d) => sum + d.amount, 0);
                                    return totalB - totalA;
                                })
                                .map((installer) => {
                                    const rowTotal = Object.values(installer.dates).reduce(
                                        (sum, d) => sum + d.amount,
                                        0
                                    );
                                    const totalJournals = Object.values(installer.dates).reduce(
                                        (sum, d) => sum + d.journals,
                                        0
                                    );
                                    const dateEntries = allDates
                                        .map((date) => ({ date, data: installer.dates[date] }))
                                        .filter(({ data }) => !!data);

                                    return (
                                        <MobileInstallerCard key={installer.id}>
                                            <MobileInstallerHeader>
                                                <div>
                                                    <MobileInstallerName>{installer.name}</MobileInstallerName>
                                                    <MobileInstallerMeta>
                                                        <span>RM {rowTotal.toFixed(2)}</span>
                                                        <span>•</span>
                                                        <span>{totalJournals} journal(s)</span>
                                                    </MobileInstallerMeta>
                                                </div>
                                                <MobileBadge>Total</MobileBadge>
                                            </MobileInstallerHeader>

                                            <MobileDateList>
                                                {dateEntries.length > 0 ? (
                                                    dateEntries.map(({ date, data }) => {
                                                        const journalTimes = (data.journalList || [])
                                                            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                                                            .map((j) => moment(j.createdAt).format("h:mm a"));
                                                        const timesDisplay = journalTimes.join(", ");
                                                        return (
                                                            <MobileDateItem
                                                                key={date}
                                                                onClick={() => {
                                                                    setSelectedDayJournals(data.journalList || []);
                                                                    setSelectedDayDate(date);
                                                                    setDayDetailModalOpen(true);
                                                                }}
                                                                style={{ cursor: "pointer" }}
                                                            >
                                                                <MobileDateLabel>
                                                                    {date}
                                                                    <MobileJournalCount>
                                                                        ({data.journals})
                                                                    </MobileJournalCount>
                                                                </MobileDateLabel>
                                                                <MobileAmount>{timesDisplay || "-"}</MobileAmount>
                                                            </MobileDateItem>
                                                        );
                                                    })
                                                ) : (
                                                    <MobileEmpty>No journal entries in this range</MobileEmpty>
                                                )}
                                            </MobileDateList>
                                        </MobileInstallerCard>
                                    );
                                })}

                            {!installer && (
                                <MobileTotalsCard>
                                    <MobileTotalsTitle>Daily Totals</MobileTotalsTitle>
                                    <MobileDateList>
                                        {allDates
                                            .map((date) => ({ date, amount: dailyTotals[date] || 0 }))
                                            .filter(({ amount }) => amount > 0)
                                            .map(({ date, amount }) => (
                                                <MobileDateItem key={date}>
                                                    <MobileDateLabel>{date}</MobileDateLabel>
                                                    <MobileAmount>RM {amount.toFixed(2)}</MobileAmount>
                                                </MobileDateItem>
                                            ))}
                                        {Object.values(dailyTotals).reduce((sum, amt) => sum + amt, 0) === 0 && (
                                            <MobileEmpty>No totals for this range</MobileEmpty>
                                        )}
                                    </MobileDateList>
                                </MobileTotalsCard>
                            )}
                        </DailyBreakdownMobile>
                    </Section>
                ) : (
                    <Section>
                        <EmptyState>
                            <FiFileText />
                            <h4>No Journals Found</h4>
                            <p>No journal entries found for the selected date range.</p>
                        </EmptyState>
                    </Section>
                )}

                {/* Journal Entries List */}
                {journals.length > 0 && (
                    <Section>
                        <SectionTitle>
                            <FiFileText />
                            Journals ({journals.length})
                        </SectionTitle>
                        <JournalsGrid>
                            {journals.map((journal, index) => {
                                // Calculate amount per installer for THIS journal only
                                const installersInThisJournal = journal.installers?.length || 0;
                                const isServiceType = journal.type === "service";
                                const amountPerInstaller = installersInThisJournal > 0 && !isServiceType
                                    ? (journal.amountPerInstaller || 0)
                                    : 0;

                                return (
                                    <JournalCard key={journal._id || index}>
                                        <JournalCardHeader>
                                            <JournalDate>
                                                <FiCalendar />
                                                {moment(journal.createdAt).format("DD-MMM")} {moment(journal.createdAt).format("h:mm a")}
                                                {isServiceType && (
                                                    <span style={{ marginLeft: '8px', background: 'rgba(249, 115, 22, 0.3)', color: '#fb923c', padding: '2px 8px', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: '700' }}>
                                                        Service
                                                    </span>
                                                )}
                                            </JournalDate>
                                        </JournalCardHeader>
                                        
                                        <JournalCardBody>
                                            <JournalSection>
                                                <JournalLabel>Order:</JournalLabel>
                                                <JournalValue>
                                                    {journal.order?.invoiceId || "N/A"} - {journal.order?.coNumber || "N/A"}
                                                </JournalValue>
                                            </JournalSection>
                                            
                                            <JournalSection>
                                                <JournalLabel>Customer:</JournalLabel>
                                                <JournalValue>
                                                    {journal.order?.userId?.name || "Unknown"}
                                                </JournalValue>
                                            </JournalSection>
                                            
                                            <JournalSection>
                                                <JournalLabel>Address:</JournalLabel>
                                                <JournalValue>
                                                    {journal.order?.address || "Not provided"}
                                                </JournalValue>
                                            </JournalSection>
                                            
                                            <JournalSection>
                                                <JournalLabel>Installers ({installersInThisJournal}):</JournalLabel>
                                                <InstallersGrid>
                                                    {journal.installers && journal.installers.length > 0 ? (
                                                        journal.installers.map((installer, idx) => (
                                                            <InstallerTag key={idx}>
                                                                <FiUsers />
                                                                <span>{installer.name || "Unknown"}</span>
                                                                <InstallerAmount>
                                                                    {isServiceType ? "-" : `RM ${amountPerInstaller.toFixed(2)}`}
                                                                </InstallerAmount>
                                                            </InstallerTag>
                                                        ))
                                                    ) : (
                                                        <JournalValue>No installers assigned</JournalValue>
                                                    )}
                                                </InstallersGrid>
                                            </JournalSection>
                                            
                                            {journal.totalInstallerEntries > 0 && !isServiceType && (
                                                <AmountBreakdownSection>
                                                    <JournalLabel>Amount Breakdown:</JournalLabel>
                                                    <BreakdownGrid>
                                                        <BreakdownItem>
                                                            <BreakdownLabel>Order Total:</BreakdownLabel>
                                                            <BreakdownValue>RM {(journal.order?.amount || 0).toFixed(2)}</BreakdownValue>
                                                        </BreakdownItem>
                                                        <BreakdownItem>
                                                            <BreakdownLabel>Total Entries:</BreakdownLabel>
                                                            <BreakdownValue>{journal.totalInstallerEntries} installers</BreakdownValue>
                                                        </BreakdownItem>
                                                        <BreakdownItem>
                                                            <BreakdownLabel>Per Entry:</BreakdownLabel>
                                                            <BreakdownValue>RM {(journal.amountPerInstaller || 0).toFixed(2)}</BreakdownValue>
                                                        </BreakdownItem>
                                                    </BreakdownGrid>
                                                </AmountBreakdownSection>
                                            )}
                                        </JournalCardBody>
                                        
                                        <JournalCardFooter>
                                            <span>Created {moment(journal.createdAt).fromNow()}</span>
                                            {(superadmin || staff || installerManager) && (
                                                <JournalActions>
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEditJournal(journal);
                                                        }}
                                                        title="Edit Journal"
                                                    >
                                                        <FiEdit2 />
                                                    </IconButton>
                                                    <IconButton
                                                        className="delete"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteJournal(journal._id);
                                                        }}
                                                        title="Delete Journal"
                                                    >
                                                        <FiTrash2 />
                                                    </IconButton>
                                                </JournalActions>
                                            )}
                                        </JournalCardFooter>
                                    </JournalCard>
                                );
                            })}
                        </JournalsGrid>
                    </Section>
                )}

                {/* Edit Modal */}
                {editModalOpen && selectedJournal && (
                    <ModalOverlay onClick={() => setEditModalOpen(false)}>
                        <ModalContent onClick={(e) => e.stopPropagation()}>
                            <ModalHeader>
                                <ModalTitle>Edit Journal</ModalTitle>
                                <CloseButton onClick={() => setEditModalOpen(false)}>
                                    <FiX />
                                </CloseButton>
                            </ModalHeader>
                            
                            <FormGroup>
                                <Label>Order</Label>
                                <div style={{ color: '#e2e8f0', padding: '0.75rem', background: 'rgba(30, 41, 59, 0.5)', borderRadius: '8px' }}>
                                    {selectedJournal.order?.invoiceId || "N/A"} - {selectedJournal.order?.coNumber || "N/A"}
                                </div>
                            </FormGroup>

                            <FormGroup>
                                <Label>Installers *</Label>
                                <Select
                                    isMulti
                                    options={allInstallers.map((installer) => ({
                                        label: installer.name,
                                        value: installer._id,
                                    }))}
                                    value={allInstallers
                                        .filter((installer) => 
                                            editFormData.installers.includes(installer._id)
                                        )
                                        .map((installer) => ({
                                            label: installer.name,
                                            value: installer._id,
                                        }))
                                    }
                                    onChange={(selected) => {
                                        setEditFormData({
                                            ...editFormData,
                                            installers: selected ? selected.map(s => s.value) : [],
                                        });
                                    }}
                                    placeholder="Select installers..."
                                    {...commonSelectProps}
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label>Created Date & Time</Label>
                                <DateInput
                                    type="datetime-local"
                                    value={editFormData.createdAt}
                                    onChange={(e) => 
                                        setEditFormData({
                                            ...editFormData,
                                            createdAt: e.target.value,
                                        })
                                    }
                                />
                            </FormGroup>

                            <ModalActions>
                                <Button 
                                    className="secondary"
                                    onClick={() => setEditModalOpen(false)}
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    className="primary"
                                    onClick={handleUpdateJournal}
                                >
                                    Update Journal
                                </Button>
                            </ModalActions>
                        </ModalContent>
                    </ModalOverlay>
                )}

                {/* Day Detail Modal - Journal cards for selected day (mobile daily breakdown click) */}
                {dayDetailModalOpen && selectedDayJournals.length > 0 && (
                    <ModalOverlay onClick={() => setDayDetailModalOpen(false)}>
                        <ModalContent 
                            onClick={(e) => e.stopPropagation()} 
                            style={{ maxHeight: '90vh', overflowY: 'auto' }}
                        >
                            <ModalHeader>
                                <ModalTitle>Journals - {selectedDayDate}</ModalTitle>
                                <CloseButton onClick={() => setDayDetailModalOpen(false)}>
                                    <FiX />
                                </CloseButton>
                            </ModalHeader>
                            <JournalsGrid>
                                {selectedDayJournals.map((journal, index) => {
                                    const installersInThisJournal = journal.installers?.length || 0;
                                    const isServiceType = journal.type === "service";
                                    const amountPerInstaller = installersInThisJournal > 0 && !isServiceType
                                        ? (journal.amountPerInstaller || 0)
                                        : 0;

                                    return (
                                        <JournalCard key={journal._id || index}>
                                            <JournalCardHeader>
                                                <JournalDate>
                                                    <FiCalendar />
                                                    {moment(journal.createdAt).format("DD-MMM")} {moment(journal.createdAt).format("h:mm a")}
                                                    {isServiceType && (
                                                        <span style={{ marginLeft: '8px', background: 'rgba(249, 115, 22, 0.3)', color: '#fb923c', padding: '2px 8px', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: '700' }}>
                                                            Service
                                                        </span>
                                                    )}
                                                </JournalDate>
                                            </JournalCardHeader>
                                            
                                            <JournalCardBody>
                                                <JournalSection>
                                                    <JournalLabel>Order:</JournalLabel>
                                                    <JournalValue>
                                                        {journal.order?.invoiceId || "N/A"} - {journal.order?.coNumber || "N/A"}
                                                    </JournalValue>
                                                </JournalSection>
                                                
                                                <JournalSection>
                                                    <JournalLabel>Customer:</JournalLabel>
                                                    <JournalValue>
                                                        {journal.order?.userId?.name || "Unknown"}
                                                    </JournalValue>
                                                </JournalSection>
                                                
                                                <JournalSection>
                                                    <JournalLabel>Address:</JournalLabel>
                                                    <JournalValue>
                                                        {journal.order?.address || "Not provided"}
                                                    </JournalValue>
                                                </JournalSection>
                                                
                                                <JournalSection>
                                                    <JournalLabel>Installers ({installersInThisJournal}):</JournalLabel>
                                                    <InstallersGrid>
                                                        {journal.installers && journal.installers.length > 0 ? (
                                                            journal.installers.map((installer, idx) => (
                                                                <InstallerTag key={idx}>
                                                                    <FiUsers />
                                                                    <span>{installer.name || "Unknown"}</span>
                                                                    <InstallerAmount>
                                                                        {isServiceType ? "-" : `RM ${amountPerInstaller.toFixed(2)}`}
                                                                    </InstallerAmount>
                                                                </InstallerTag>
                                                            ))
                                                        ) : (
                                                            <JournalValue>No installers assigned</JournalValue>
                                                        )}
                                                    </InstallersGrid>
                                                </JournalSection>
                                                
                                                {journal.totalInstallerEntries > 0 && !isServiceType && (
                                                    <AmountBreakdownSection>
                                                        <JournalLabel>Amount Breakdown:</JournalLabel>
                                                        <BreakdownGrid>
                                                            <BreakdownItem>
                                                                <BreakdownLabel>Order Total:</BreakdownLabel>
                                                                <BreakdownValue>RM {(journal.order?.amount || 0).toFixed(2)}</BreakdownValue>
                                                            </BreakdownItem>
                                                            <BreakdownItem>
                                                                <BreakdownLabel>Total Entries:</BreakdownLabel>
                                                                <BreakdownValue>{journal.totalInstallerEntries} installers</BreakdownValue>
                                                            </BreakdownItem>
                                                            <BreakdownItem>
                                                                <BreakdownLabel>Per Entry:</BreakdownLabel>
                                                                <BreakdownValue>RM {(journal.amountPerInstaller || 0).toFixed(2)}</BreakdownValue>
                                                            </BreakdownItem>
                                                        </BreakdownGrid>
                                                    </AmountBreakdownSection>
                                                )}
                                            </JournalCardBody>
                                            
                                            <JournalCardFooter>
                                                <span>Created {moment(journal.createdAt).fromNow()}</span>
                                                {(superadmin || staff || installerManager) && (
                                                    <JournalActions>
                                                        <IconButton
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setDayDetailModalOpen(false);
                                                                handleEditJournal(journal);
                                                            }}
                                                            title="Edit Journal"
                                                        >
                                                            <FiEdit2 />
                                                        </IconButton>
                                                        <IconButton
                                                            className="delete"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteJournal(journal._id, () => setDayDetailModalOpen(false));
                                                            }}
                                                            title="Delete Journal"
                                                        >
                                                            <FiTrash2 />
                                                        </IconButton>
                                                    </JournalActions>
                                                )}
                                            </JournalCardFooter>
                                        </JournalCard>
                                    );
                                })}
                            </JournalsGrid>
                        </ModalContent>
                    </ModalOverlay>
                )}
            </DashboardContainer>
        </DashboardBg>
    );
};

export default Journal;
